import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ExternalLink } from 'lucide-react';

// Renders the details of a booking that is blocking a slot, plus a link that
// navigates to /bookings and auto-opens that booking's edit dialog.
export function ConflictCard({ conflict, time, title = 'Skipped', onNavigate, className = '' }) {
  const navigate = useNavigate();
  const c = conflict || {};
  const created = (c.created_at || '').replace('T', ' ').split('.')[0];
  const hours = Math.round((c.duration_minutes || 0) / 6) / 10;

  const goEdit = () => {
    if (!c.id) return;
    if (onNavigate) onNavigate();
    navigate(`/office/bookings?edit=${encodeURIComponent(c.id)}`);
  };

  return (
    <div
      className={`p-3 bg-amber-50 border border-amber-300 rounded-lg text-xs text-amber-900 ${className}`}
      data-testid="conflict-card"
    >
      <div className="flex items-center gap-2 flex-wrap mb-1.5">
        <AlertCircle className="w-4 h-4 text-amber-700" />
        <span className="font-semibold">
          {title}{time ? `: ${time}` : ''}
        </span>
        <span className="text-amber-700">— overlaps with existing booking</span>
        {c.is_bulk && (
          <span className="px-2 py-0.5 rounded-full bg-blue-600 text-white text-[10px] font-bold">
            BULK ({hours}h)
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 pl-6">
        <div><strong>Booked by:</strong> {c.player_name || 'N/A'} ({c.player_mobile || 'N/A'})</div>
        <div><strong>Game:</strong> {c.game || 'N/A'}</div>
        <div><strong>Court:</strong> {c.court || 'N/A'}</div>
        <div><strong>Time:</strong> {c.start_time} – {c.end_time}</div>
        <div><strong>Platform:</strong> {c.booking_source || 'N/A'}</div>
        <div><strong>Booking ID:</strong> <code className="bg-white px-1 rounded">{c.booking_id || 'N/A'}</code></div>
        {c.platform_booking_id ? (
          <div className="sm:col-span-2">
            <strong>Platform Booking ID:</strong>{' '}
            <code className="bg-white px-1 rounded font-mono">{c.platform_booking_id}</code>
          </div>
        ) : null}
        {created && <div className="sm:col-span-2"><strong>Booked at:</strong> {created}</div>}
      </div>
      {c.id ? (
        <div className="pl-6 mt-2">
          <button
            type="button"
            onClick={goEdit}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-amber-600 text-white font-semibold hover:bg-amber-700 transition-colors"
            data-testid="conflict-edit-link"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Edit this booking
          </button>
        </div>
      ) : null}
    </div>
  );
}
